// Unpaid Transactions — every open order (any balance, including zero)
// the front desk still needs to close. Real-time via the same live-sync
// hooks the calendar uses; the list re-fetches within a debounce
// whenever the server broadcasts an order or appointment update.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";
import * as live from "@/lib/liveSync";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function UnpaidTransactionsReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");
  const refreshTimer = useRef(null);

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/unpaid-transactions`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  // Real-time: any order or appointment update at this branch triggers
  // a debounced refetch so a Close Order or void performed elsewhere
  // updates the list without a manual refresh.
  useEffect(() => {
    const scheduleRefresh = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => { load(); }, 400);
    };
    const offOrder = live.subscribe("order_updated", (payload) => {
      if (!payload || payload.branch_id === bid || !payload.branch_id) scheduleRefresh();
    });
    const offAppt = live.subscribe("appointment_updated", () => scheduleRefresh());
    return () => {
      offOrder && offOrder();
      offAppt && offAppt();
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [bid, load]);

  const rows = data?.rows || [];

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers on Nominal + Balance Due.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const num = { numeric: true, format: money, align: "right" };
    return {
      filename: "unpaid-transactions",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Date",        key: "date" },
        { header: "Order",       key: "order_number" },
        { header: "Guest",       key: "guest_name" },
        { header: "Treatments",  key: "treatments" },
        { header: "Nominal",     key: "nominal",     ...num },
        { header: "Balance Due", key: "balance_due", ...num },
      ],
      rows: rows.map((r) => ({
        date: fmtDate(r.date),
        order_number: `#${r.order_number || "—"}`,
        guest_name: r.guest_name,
        treatments: (r.treatments || []).join(" · "),
        nominal: Number(r.nominal) || 0,
        balance_due: Number(r.balance_due) || 0,
      })),
      footerRow: [
        "Totals", "", "", `${rows.length} order${rows.length === 1 ? "" : "s"}`,
        Number(data.total_nominal) || 0, Number(data.total_balance) || 0,
      ],
    };
  }, [data, rows, branch]);

  return (
    <ReportShell
      title="Unpaid Transactions"
      subtitle="Every order that hasn't been marked Closed — including zero-balance carts still waiting on the Close Order tap."
      branchName={branch?.name}
      tab="sales"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="unpaid-transactions-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Unpaid Transactions</h2>
              </div>
              {data && (
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open orders</div>
                    <div className="text-xl font-semibold" data-testid="unpaid-count">{data.count}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total nominal</div>
                    <div className="text-xl font-semibold" data-testid="unpaid-total-nominal">{formatBranchMoney(branch, data.total_nominal)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance due</div>
                    <div className="text-xl font-semibold" data-testid="unpaid-total-balance">{formatBranchMoney(branch, data.total_balance)}</div>
                  </div>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider w-32">Date</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Guest Name</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Treatment / Package</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right w-40">Nominal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8" data-testid="unpaid-empty">
                      {loading ? "Loading…" : "No open orders in this range."}
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.order_id} className="hover:bg-secondary/30" data-testid={`unpaid-row-${r.order_id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-sm">
                      {r.guest_id ? (
                        <Link to={`/staff/brand/customers/${r.guest_id}`} className="text-primary hover:underline">
                          {r.guest_name}
                        </Link>
                      ) : r.guest_name}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {(r.treatments || []).length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (r.treatments || []).map((t, i) => (
                          <span key={i} className="text-foreground">{i > 0 && <span className="text-muted-foreground mr-1">·</span>}{t}</span>
                        ))}
                        {r.zero_balance && (
                          <Badge variant="outline" className="ml-1 border-emerald-300 text-emerald-800 bg-emerald-50" data-testid={`unpaid-zero-balance-${r.order_id}`}>
                            Zero balance
                          </Badge>
                        )}
                        {r.outside_range && (
                          <Badge variant="outline" className="ml-1 border-amber-300 text-amber-900 bg-amber-50" data-testid={`unpaid-outside-range-${r.order_id}`}>
                            Outside range · still owed
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      <Link
                        to={`/staff/branch/${bid}/orders?order=${r.order_id}`}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`unpaid-open-order-${r.order_id}`}
                      >
                        {formatBranchMoney(branch, r.nominal)}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
                {data && rows.length > 0 && (
                  <TableRow className="bg-muted font-semibold" data-testid="unpaid-totals-row">
                    <TableCell colSpan={2}>Totals · {data.count} order{data.count === 1 ? "" : "s"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {data.zero_balance_count} zero-balance
                    </TableCell>
                    <TableCell className="text-right">{formatBranchMoney(branch, data.total_nominal)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
